import { gql } from '@apollo/client';
import { apolloClient } from 'apollo-client';
import { QualificationType, FormFieldValue } from '../configs/types';
import { fetchCounties } from './fetch-counties';
import {
  fetchLmiProofDocLabelOptions,
  Category,
} from './fetch-lmi-proof-options';
import {
  fetchCurrentLmiContract,
  ContractVersion,
} from './fetch-current-lmi-contract';

export const normalizeQualificationTypeSelection = (
  qualificationType: FormFieldValue
): string[] => {
  const selected = Array.isArray(qualificationType)
    ? (qualificationType as string[])
    : [];

  return selected.includes(QualificationType.MN_COMBINED)
    ? [QualificationType.PROGRAM, QualificationType.INCOME]
    : selected;
};

export const CREATE_PERSONALIZED_CONTRACT_PREVIEW = gql`
  mutation CreatePersonalizedContractPreview(
    $input: CreatePersonalizedContractPreviewInput!
  ) {
    createPersonalizedContractPreview(input: $input) {
      base64
    }
  }
`;

export const CREATE_CONTRACT_AGREEMENTS = gql`
  mutation CreateContractAgreements($input: [CreateContractAgreementInput!]!) {
    createContractAgreements(input: $input) {
      success
    }
  }
`;

export interface TabsDataInput {
  county: string | null;
  houseOccupancy: string | null;
  houseIncome: string | null;
  lmiProgram: string[] | null;
  lmiSourceSelection: string[] | null;
}

export interface CreatePersonalizedContractPreviewInput {
  contractVersionId: number;
  utilityAccountId: number;
  tabsData: TabsDataInput;
}

export interface CreateContractAgreementInput {
  contractVersionId: number;
  userId: number;
  userAgent?: string;
  utilityAccountId: number;
  contractVersionLanguageId?: number;
}

export interface LmiContractData {
  counties: string[];
  incomeLimits: Record<string, string[]>;
  programOptions: Record<string, string | string[]> | null;
  contract: ContractVersion | null;
}

export interface FetchLmiContractDataParams {
  userId: number;
  zipCode: string;
  utilityId: number;
  stateId: number;
  category: Category;
  tag?: string[];
}

export const fetchLmiContractData = async (
  params: FetchLmiContractDataParams
): Promise<LmiContractData> => {
  const [countiesResult, programsResult, contractResult] = await Promise.all([
    fetchCounties({ id: params.userId, zipCode: params.zipCode }),
    fetchLmiProofDocLabelOptions({
      utilityId: params.utilityId,
      category: params.category,
    }),
    fetchCurrentLmiContract({ stateId: params.stateId, tag: params.tag }),
  ]);

  return {
    counties: countiesResult.counties,
    incomeLimits: countiesResult.incomeLimits,
    programOptions: programsResult.data,
    contract: contractResult,
  };
};

export const createPersonalizedContractPreview = async (
  input: CreatePersonalizedContractPreviewInput
): Promise<{ base64: string }> => {
  const { data } = await apolloClient.mutate({
    mutation: CREATE_PERSONALIZED_CONTRACT_PREVIEW,
    variables: { input },
  });

  return data.createPersonalizedContractPreview;
};

export const createContractAgreements = async (
  input: CreateContractAgreementInput[]
): Promise<Array<{ success: boolean }>> => {
  const { data } = await apolloClient.mutate({
    mutation: CREATE_CONTRACT_AGREEMENTS,
    variables: { input },
  });

  return data.createContractAgreements;
};

export const prepareTabsDataForPreview = (
  formData: {
    county?: string | null;
    houseOccupancy?: string | null;
    houseIncome?: string | null;
    lmiProgram?: string | string[] | null;
    lmiSourceSelection?: string[] | null;
  },
  qualificationType: string | string[]
): TabsDataInput => {
  let lmiSourceSelection: string[] = [];

  const isRadio = !Array.isArray(qualificationType);
  const isIncomeRadio =
    isRadio && qualificationType === QualificationType.INCOME;
  const isIncomeRejectedRadio =
    isRadio && qualificationType === QualificationType.INCOME_REJECTED;

  if (Array.isArray(qualificationType)) {
    lmiSourceSelection = normalizeQualificationTypeSelection(qualificationType);
  } else if (qualificationType) {
    lmiSourceSelection = [qualificationType];
  }

  // houseIncome: only for INCOME radio or checkbox states (MN/VA)
  const shouldSendIncome = !isRadio || isIncomeRadio;

  // lmiProgram: suppress for INCOME / INCOME_REJECTED radio selections
  const shouldSendProgram = !isIncomeRadio && !isIncomeRejectedRadio;

  // Normalise lmiProgram to string[] for the API
  const lmiProgramArray = (() => {
    if (!shouldSendProgram || !formData.lmiProgram) return null;
    if (Array.isArray(formData.lmiProgram)) {
      return formData.lmiProgram.length > 0 ? formData.lmiProgram : null;
    }
    return [formData.lmiProgram as string];
  })();

  return {
    county: formData.county || null,
    houseOccupancy: formData.houseOccupancy || null,
    houseIncome: shouldSendIncome ? formData.houseIncome || null : null,
    lmiProgram: lmiProgramArray,
    lmiSourceSelection:
      lmiSourceSelection.length > 0 ? lmiSourceSelection : null,
  };
};
